/**
 * navStack — the pure state machine behind in-app "Back".
 *
 * WHY THIS EXISTS
 * The old implementation recorded every `location` change as a new history
 * entry. That produced three user-visible bugs:
 *
 *  1. Filter/sort/search hooks write the URL with `replace: true`. Those writes
 *     changed `location.search`, so each keystroke stacked another entry for the
 *     *same* page — Back returned to an older filter state instead of the page
 *     the user came from.
 *  2. A browser Back is also a `location` change, so it *grew* the stack. The
 *     next in-app Back walked the user forward again (ping-pong).
 *  3. Back navigated by pushing a new entry, so the browser's own Back button
 *     went straight back into the page just left.
 *
 * The fix is to classify each change by router navigation type and to unwind
 * *real* browser history (`navigate(delta)`) whenever the previous in-app entry
 * is genuinely reachable that way. Because sort/filter state lives in the URL,
 * unwinding history restores the user's view for free.
 */

export interface NavEntry {
  pathname: string;
  search: string;
  /** React Router's history index (`window.history.state.idx`). */
  idx: number;
}

export interface NavStackState {
  /** Previous in-app entries, oldest first. */
  entries: NavEntry[];
  /** The entry currently on screen. */
  current: NavEntry | null;
}

export type NavAction = 'PUSH' | 'REPLACE' | 'POP';

export interface NavEvent {
  action: NavAction;
  pathname: string;
  search: string;
  idx: number;
}

export type BackPlan =
  | { kind: 'history-back'; delta: number }
  | { kind: 'fallback'; path: string };

export const MAX_STACK_SIZE = 20;
/** Never unwind more than this in one click — a runaway delta is a bug, not intent. */
export const MAX_BACK_DELTA = 10;

export const emptyNavStack: NavStackState = { entries: [], current: null };

function sameLocation(a: NavEntry, b: NavEntry): boolean {
  return a.pathname === b.pathname && a.search === b.search;
}

export function reduceNavStack(state: NavStackState, event: NavEvent): NavStackState {
  const next: NavEntry = {
    pathname: event.pathname,
    search: event.search,
    idx: event.idx,
  };

  // REPLACE never creates history depth (filter/sort/search writes, redirects).
  if (event.action === 'REPLACE') {
    return { entries: state.entries, current: next };
  }

  if (event.action === 'POP') {
    // Browser Back/Forward: the stack must shrink to entries strictly older
    // than where we landed, never grow.
    const entries = state.entries.filter((e) => e.idx < next.idx);
    return { entries, current: next };
  }

  // PUSH
  if (!state.current) return { entries: state.entries, current: next };
  if (sameLocation(state.current, next)) return { entries: state.entries, current: next };

  // Collapse consecutive entries for the same page: a query-only change is not
  // a place the user wants to come "back" to.
  if (state.current.pathname === next.pathname) {
    return { entries: state.entries, current: next };
  }

  const entries = [...state.entries, state.current].slice(-MAX_STACK_SIZE);
  return { entries, current: next };
}

/**
 * Decide what Back should do. Unwinding real browser history keeps the browser
 * and the app in sync; anything we cannot prove reachable falls back to the
 * logical parent route.
 */
export function planBack(state: NavStackState, fallbackPath: string): BackPlan {
  const { current, entries } = state;
  const top = entries[entries.length - 1];

  if (current && top && current.idx > 0 && top.idx >= 0 && top.idx < current.idx) {
    const delta = current.idx - top.idx;
    if (delta <= MAX_BACK_DELTA && top.pathname !== current.pathname) {
      return { kind: 'history-back', delta: -delta };
    }
  }

  return { kind: 'fallback', path: fallbackPath };
}

export function previousPathOf(state: NavStackState): string | null {
  const top = state.entries[state.entries.length - 1];
  return top ? top.pathname : null;
}

const STORAGE_KEY = 'nav_stack_v1';

export function serializeNavStack(state: NavStackState): string {
  return JSON.stringify({ entries: state.entries, current: state.current });
}

export function parseNavStack(raw: string | null): NavStackState {
  if (!raw) return emptyNavStack;
  try {
    const parsed = JSON.parse(raw) as Partial<NavStackState>;
    const clean = (e: unknown): NavEntry | null => {
      const v = e as Partial<NavEntry> | null;
      if (!v || typeof v.pathname !== 'string') return null;
      return {
        pathname: v.pathname,
        search: typeof v.search === 'string' ? v.search : '',
        idx: typeof v.idx === 'number' ? v.idx : 0,
      };
    };
    const entries = Array.isArray(parsed.entries)
      ? (parsed.entries.map(clean).filter(Boolean) as NavEntry[]).slice(-MAX_STACK_SIZE)
      : [];
    return { entries, current: clean(parsed.current) };
  } catch {
    return emptyNavStack;
  }
}

/** Survives full reloads (lazy-chunk auto-reload, stale-JWT reboot, OAuth handoff). */
export function loadNavStack(): NavStackState {
  try {
    return parseNavStack(sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return emptyNavStack;
  }
}

export function saveNavStack(state: NavStackState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, serializeNavStack(state));
  } catch {
    /* private mode / quota — Back degrades to the fallback route */
  }
}

/** Current router history index, or 0 when unavailable (SSR, tests). */
export function readHistoryIndex(): number {
  try {
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    return typeof idx === 'number' ? idx : 0;
  } catch {
    return 0;
  }
}
